import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import WebApp from '@twa-dev/sdk'

interface TelegramUserView {
  id: string
  name: string
}

export interface TelegramBootstrapState {
  isReady: boolean
  isTelegram: boolean
  source: 'telegram' | 'browser-fallback'
  initData: string
  telegramUser?: TelegramUserView
  fallbackTelegramId?: string
  fallbackRole?: 'student' | 'teacher'
  bootstrapError?: string
}

const initialValue: TelegramBootstrapState = {
  isReady: false,
  isTelegram: false,
  source: 'browser-fallback',
  initData: '',
}

const TelegramContext = createContext<TelegramBootstrapState>(initialValue)

const resolveFallbackTelegramId = (): string | undefined => {
  const queryValue = new URLSearchParams(window.location.search).get('mockTelegramId')
  const envValue = import.meta.env.VITE_TMA_FALLBACK_TELEGRAM_ID
  const candidate = queryValue ?? envValue
  const normalized = candidate?.trim()
  return normalized ? normalized : undefined
}

const resolveFallbackRole = (): 'student' | 'teacher' | undefined => {
  const queryValue = new URLSearchParams(window.location.search).get('mockRole')
  const envValue = import.meta.env.VITE_TMA_FALLBACK_ROLE
  const candidate = String(queryValue ?? envValue ?? '')
    .trim()
    .toLowerCase()

  if (candidate === 'teacher' || candidate === 'trainer') {
    return 'teacher'
  }

  if (candidate === 'student') {
    return 'student'
  }

  return undefined
}

const hasTelegramWebApp = (): boolean => {
  if (typeof window === 'undefined') {
    return false
  }

  const runtime = (window as Window & { Telegram?: { WebApp?: unknown } }).Telegram?.WebApp
  return Boolean(runtime)
}

const hasReliableTelegramContext = (): boolean => {
  const initData = WebApp.initData?.trim()
  const userId = WebApp.initDataUnsafe.user?.id

  return Boolean(initData) || typeof userId === 'number'
}

export const TelegramProvider = ({ children }: { children: ReactNode }) => {
  const [state, setState] = useState<TelegramBootstrapState>(initialValue)

  useEffect(() => {
    const fallbackTelegramId = resolveFallbackTelegramId()
    const fallbackRole = resolveFallbackRole()

    if (!hasTelegramWebApp()) {
      setState({
        isReady: true,
        isTelegram: false,
        source: 'browser-fallback',
        initData: '',
        fallbackTelegramId,
        fallbackRole,
      })
      return
    }

    try {
      WebApp.ready()
      WebApp.expand()

      const telegramId = WebApp.initDataUnsafe.user?.id
      const firstName = WebApp.initDataUnsafe.user?.first_name?.trim()
      const lastName = WebApp.initDataUnsafe.user?.last_name?.trim()
      const fullName = [firstName, lastName].filter(Boolean).join(' ').trim()

      const isTelegramContext = hasReliableTelegramContext()

      setState({
        isReady: true,
        isTelegram: isTelegramContext,
        source: isTelegramContext ? 'telegram' : 'browser-fallback',
        initData: WebApp.initData || '',
        fallbackTelegramId,
        fallbackRole,
        telegramUser:
          typeof telegramId === 'number'
            ? {
                id: String(telegramId),
                name: fullName || `Telegram user #${telegramId}`,
              }
            : undefined,
      })
    } catch (error) {
      setState({
        isReady: true,
        isTelegram: false,
        source: 'browser-fallback',
        initData: '',
        fallbackTelegramId,
        fallbackRole,
        bootstrapError: error instanceof Error ? error.message : String(error),
      })
    }
  }, [])

  const value = useMemo(() => state, [state])
  return <TelegramContext.Provider value={value}>{children}</TelegramContext.Provider>
}

export const useTelegram = (): TelegramBootstrapState => useContext(TelegramContext)
